import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import type { PartnerAspect } from '../../master-data-management/partner/entities/aspects/PartnerAspect.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import type { ChancenwerkUserAspect } from '../../master-data-management/users/entities/aspects/ChancenwerkUserAspect.js';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { schoolYearLabelForStartYear, schoolYearStartYear } from '../../school-year.js';

export const meetingOccasions = [
  'Kooperationsgespräch',
  'Feedbackgespräch',
  'Netzwerkgespräch',
  'Veranstaltung',
  'Telefongespräch',
  'Mail',
  'Wichtige Infos ohne Gespräch',
  'Tür- und Angelgespräch',
  'Planungsgespräch',
  'Quartalsbericht',
  'Hospitation',
] as const;
export type MeetingOccasion = (typeof meetingOccasions)[number];

export const meetingCategory = [
  'Schüler:innen Akquise',
  'Angaben zum CW-Image',
  'Angaben zum Schulteam',
  'Digitale Anmeldung',
  'Einarbeitung ÜL',
  'Elternsprechtag',
  'Materiallagerung',
  'Monitoring',
  'OS-Akquise',
  'OS-Zertifikate',
  'Produkt wird gut genutzt/umgesetzt',
  'Projekte',
  'Schulbesuche',
  'US-Akquise',
  'Wie werden die Ergebnisse an die Teammitglieder versendet?',
  'Zusammenarbeit mit Schule',
  'Zusammenarbeit SchuKo',
  'CHANCENakademie',
] as const;
export type MeetingCategory = (typeof meetingCategory)[number];

export const meetingCategoryByQuartal = [
  [
    'Lehrerkonferenzen',
    'Klassenpflegschaftsitzung (Eltern)',
    'OS Akquise',
    'US Akquise',
    'Start der LF',
    'Chancenwerk Elternabend',
    'Schuljahresstart',
    'CHANCENREISE',
  ],
  [
    'Elternsprechtag',
    'Akquise: ggf. Leherkonferenz',
    // 'Akquise: ggf. Klassenrundgänge',
    'Tag der offenen Tür',
    'Monitoring',
    // 'US-Workshops in der LF',
    'Stammtische',
    'Weihnachtsaktion',
  ],
  [
    'Planunsgespräche',
    'Zeugnisse - HJ Wechsel',
    'Chancenreise: OS-Akquise',
    'US-Akquise',
    'Wirkungsmessung',
    // 'Stammtisch',
    'US-Workshops',
    'Osteraktion',
  ],
  [
    'Elternsprechtag',
    'Chancenreise',
    // 'Zertifikate OS',
    'Kennenlernnachmittage',
    'Planunsgespräche',
    'Schulfeste',
    'Ende der LF',
    'SchuKo-Event',
  ],
];

export function buildMeetingNoteYearArray(isQuartalsbericht = false) {
  const today = DateTime.now();
  let start = today.minus({ years: 5 });
  const end = today.plus({ years: 4 });
  const years: string[] = [];

  while (start.startOf('year') < end.startOf('year')) {
    const name = `SJ ${start.year.toString().slice(-2)}/${(start.year + 1).toString().slice(-2)}`;
    if (isQuartalsbericht && start.year >= 2024) {
      years.push(`${name} Q1`);
      years.push(`${name} Q2`);
      years.push(`${name} Q3`);
      years.push(`${name} Q4`);
    } else {
      years.push(name);
    }
    start = start.plus({ years: 1 });
  }
  return years;
}

@Entity()
export class NoteFilter extends AdornisFilter {
  public static override _class = 'NoteFilter';

  @Field(() => String) kooperationspartnerID: Maybe<string>;
  @Field(() => [String]) gespraechsfuehrungChancenWerkIDs: Maybe<string[]>;
  @Field(() => [String]) weitereBeteiligtePersonenIDs: Maybe<string[]>;

  constructor(doc: PartialEntityData<NoteFilter>) {
    super(doc);
  }

  // eslint-disable-next-line complexity
  override getMongoFilter(): Dictionary<any> {
    if (this.searchFields?.length) throw new Error('passing searchFields to NoteFilter wont do anything, please dont');

    const narrowing: object[] = [];

    const gespraechsfuehrungChancenWerkAspect = this.getFilterAspect<ChancenwerkUserAspect>(
      'gespraechsfuehrungChancenWerk',
    );
    const betKooperationspartnerAspect = this.getFilterAspect<PartnerAspect>('betKooperationspartner');
    const weitereBeteiligtePersonenAspect = this.getFilterAspect<ChancenwerkUserAspect>('weitereBeteiligtePersonen');
    const createdPersonAspect = this.getFilterAspect<ChancenwerkUserAspect>('createdPerson');

    if (createdPersonAspect?.value && createdPersonAspect.value.length > 0)
      narrowing.push({ creatorID: { $in: createdPersonAspect.value } });
    if (gespraechsfuehrungChancenWerkAspect?.value && gespraechsfuehrungChancenWerkAspect.value.length > 0)
      narrowing.push({ gespraechsfuehrungChancenWerkIDs: { $in: gespraechsfuehrungChancenWerkAspect.value } });
    if (betKooperationspartnerAspect?.value && betKooperationspartnerAspect.value.length > 0)
      narrowing.push({ kooperationspartnerID: { $in: betKooperationspartnerAspect.value } });
    if (weitereBeteiligtePersonenAspect?.value && weitereBeteiligtePersonenAspect.value.length > 0)
      narrowing.push({ weitereBeteiligtePersonenIDs: { $in: weitereBeteiligtePersonenAspect.value } });

    if (this.kooperationspartnerID) narrowing.push({ kooperationspartnerID: this.kooperationspartnerID });
    if (this.gespraechsfuehrungChancenWerkIDs)
      narrowing.push({ gespraechsfuehrungChancenWerkIDs: { $in: this.gespraechsfuehrungChancenWerkIDs ?? [] } });
    if (this.weitereBeteiligtePersonenIDs)
      narrowing.push({ weitereBeteiligtePersonenIDs: { $in: this.weitereBeteiligtePersonenIDs ?? [] } });

    // ? we have agreed with Gabriele that the views won't be searchable. I see it coming though, so I'm leaving the searchability here
    // const searchRegex = [this._safeRegex(this.search ?? '')].filter(Boolean).join('|');
    // const searchCriterium = !searchRegex
    //   ? {}
    //   : {
    //       $or: [
    //         { occasion: { $regex: searchRegex, $options: 'i' } },
    //         { note: { $regex: searchRegex, $options: 'i' } },
    //         { regard: { $regex: searchRegex, $options: 'i' } },
    //         { summary: { $regex: searchRegex, $options: 'i' } },
    //         { 'kooperationspartner.name': { $regex: searchRegex, $options: 'i' } },
    //         { 'kooperationspartner.kuerzel': { $regex: searchRegex, $options: 'i' } },
    //         { 'gespraechsfuehrungChancenWerk_single.vorname': { $regex: searchRegex, $options: 'i' } },
    //         { 'gespraechsfuehrungChancenWerk_single.email': { $regex: searchRegex, $options: 'i' } },
    //         { 'gespraechsfuehrungChancenWerk_single.nachname': { $regex: searchRegex, $options: 'i' } },
    //         { 'weitereBeteiligtePersonen_array.vorname': { $regex: searchRegex, $options: 'i' } },
    //         { 'weitereBeteiligtePersonen_array.nachname': { $regex: searchRegex, $options: 'i' } },
    //         { 'weitereBeteiligtePersonen_array.email': { $regex: searchRegex, $options: 'i' } },
    //       ],
    //     };
    // return { $and: [super.getMongoFilter(), searchCriterium] };

    return { $and: [super.getMongoFilter(), ...(Object.keys(narrowing).length ? [{ $or: narrowing }] : [])] };
  }
}

@Entity()
export class MeetingNote extends MongoEntity {
  public static override _class = 'MeetingNote';
  public static override _collectionName = 'meeting-notes';

  public static override get allFields() {
    return selectionSet(() => MeetingNote, 4);
  }

  @Field(() => String)
  public occasion: Maybe<MeetingOccasion>;

  // only used by quartalsbericht
  @Field(() => String)
  category: Maybe<MeetingCategory>;

  // only used by quartalsbericht
  @Field(() => String, {
    default: v => {
      if (v) return v;

      // Kept for notes from before the quarterly reports took over; everything from SJ 24/25 on has no
      // school year on it. The turning point itself lives in school-year.ts.
      const startYear = schoolYearStartYear();
      if (startYear < 2024) return schoolYearLabelForStartYear('SJ', startYear);

      return null;

      // TODO: quartals bereich

      // const quarters = [
      //   Interval.fromDateTimes(DateTime.fromObject({ year: DateTime.now().year, month: 1, day: 1 }), DateTime.fromObject({ year: DateTime.now().year, month: 3, day: 31 })),
      //   Interval.fromDateTimes(DateTime.fromObject({ year: DateTime.now().year, month: 4, day: 1 }), DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 })),
      //   Interval.fromDateTimes(DateTime.fromObject({ year: DateTime.now().year, month: 7, day: 1 }), DateTime.fromObject({ year: DateTime.now().year, month: 9, day: 30 })),
      //   Interval.fromDateTimes(DateTime.fromObject({ year: DateTime.now().year, month: 10, day: 1 }), DateTime.fromObject({ year: DateTime.now().year, month: 12, day: 31 })),
      // ]

      // const index = quarters.findIndex(q => q.contains(DateTime.now()));
    },
  })
  year: Maybe<string>;

  @Field(() => DateTime)
  public date: Maybe<DateTime>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  @validate(nonOptional())
  public createdAt!: DateTime;

  @Field(() => DateTime)
  public lastChangeDate!: DateTime;

  @Field(() => String)
  public note: Maybe<string>;

  @Field(() => String)
  public summary: Maybe<string>;

  @Field(() => String)
  public regard: Maybe<string>;

  /**
   * 0 = Public in Partner context
   * 1 = Only for the person who creates
   * 2 = Only for selected persons
   */
  @Field(() => Int, { default: v => v ?? 0 })
  @validate(({ value, target, key }) => {
    if (value < 0 || value > 2) throw new ValidationError('Permissions outside Range 0<p<2', { key, target });
  })
  public securityLevel: Maybe<0 | 1 | 2>;

  @Field(() => String)
  public creatorID!: string;

  @DataLoadedField(() => ChancenwerkUser, ({ creatorID }) => creatorID)
  public creator: Maybe<ChancenwerkUser>;

  @Field(() => String)
  public lastEditorID: Maybe<string>;

  @DataLoadedField(() => ChancenwerkUser, ({ lastEditorID }) => lastEditorID)
  public lastEditor: Maybe<ChancenwerkUser>;

  @Field(() => Boolean)
  isProtocolNote!: boolean;

  @Field(() => String)
  @validate(options => {
    if (!options.value?.startsWith('https://') && options.target.isProtocolNote)
      throw new ValidationError('no valid link', {
        source: options.source,
        target: options.target,
        key: options.key,
        translationKey: 'noValidLink',
      });
  })
  protocolLink: Maybe<string>;

  @Field(() => String)
  kooperationspartnerID: Maybe<string>;

  @DataLoadedField(() => Partner, ({ kooperationspartnerID }) => kooperationspartnerID)
  kooperationspartner: Maybe<Partner>;

  @Field(() => [String], { default: v => v ?? [] })
  gespraechsfuehrungChancenWerkIDs: Maybe<string[]>;

  @DataLoadedField(
    () => [Mitarbeiter],
    ({ gespraechsfuehrungChancenWerkIDs }) => gespraechsfuehrungChancenWerkIDs ?? [],
  )
  gespraechsfuehrungChancenWerk: Maybe<Mitarbeiter[]>;

  @Field(() => [String], { default: v => v ?? [] })
  weitereBeteiligtePersonenIDs!: string[];

  @DataLoadedField(() => [ChancenwerkUser], ({ weitereBeteiligtePersonenIDs }) => weitereBeteiligtePersonenIDs)
  weitereBeteiligtePersonen: Maybe<ChancenwerkUser[]>;

  @Field(() => [String], { default: v => v ?? [] })
  hospitationChancenWerkIDs: Maybe<string[]>;

  @DataLoadedField(() => [Mitarbeiter], ({ hospitationChancenWerkIDs }) => hospitationChancenWerkIDs ?? [])
  hospitationChancenWerk: Maybe<Mitarbeiter[]>;

  @Field(() => [String], { default: v => v ?? [] })
  availableToReadForUserIDs!: string[];

  @DataLoadedField(() => [ChancenwerkUser], ({ availableToReadForUserIDs }) => availableToReadForUserIDs)
  availableToReadForUsers: Maybe<ChancenwerkUser[]>;

  static override async transformBeforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: MeetingNote,
  ): Promise<void> {
    await super.transformBeforeUpdate(oldEntity, update);

    if (context.serverContext) throw new Error('cant find user on context because its a server context');
    update.lastChangeDate = DateTime.now();
    update.lastEditorID = context.userID;
  }

  override toString() {
    return `${this.occasion} vom ${this.date?.toLocaleString(DateTime.DATE_SHORT)} `;
  }
}
